import { CtaLink, Paragraph } from '@purpurds/purpur';
import { TeliaDivider, TeliaHeading, TeliaVideoPlayer } from '@teliads/components/react';
import './ContentBanner.scss';
import { useTranslation } from 'react-i18next';

import { defaultContentCardsImage } from '../card-display-variants/ContentCards';

import { TeaserRichPreamble } from 'graphql/__generated/sdk';
import { renderRichText } from 'utils/rich-text-render';
import { Dimensions, createMultipleSizeImages } from 'utils/utils';

type ContentBannerProps = {
  title: string;
  preamble: TeaserRichPreamble;
  cta: { text: string; url: string };
  image: {
    url: string;
    altText: string;
    focusArea: string;
  };
  isReversedImagePosition?: boolean;
  isDividerActive?: boolean;
  videoUrl?: string;
};

const ContentBanner = ({
  title,
  preamble,
  cta,
  isReversedImagePosition = false,
  image,
  isDividerActive = true,
  videoUrl,
}: ContentBannerProps) => {
  const contentBannerDimensions: Dimensions = {
    small: ['3:2', 650],
    medium: ['3:2', 850],
    large: ['3:2', 1280],
  };

  const imageSizes = createMultipleSizeImages(
    {
      url: image.url,
      altText: image.altText,
      focusArea: image.focusArea,
    },
    contentBannerDimensions
  );

  const { t } = useTranslation();

  return (
    <>
      <section
        className={`content-banner-section ${
          isReversedImagePosition ? 'content-banner-reverse' : ''
        }`}
      >
        <div className="content-banner-section__text-container">
          <TeliaHeading tag="h3" variant="title-300">
            {title || ''}
          </TeliaHeading>
          <Paragraph>{preamble ? renderRichText(preamble.json) : <></>}</Paragraph>

          {cta.url && (
            <div>
              <CtaLink variant="primary" href={cta.url}>
                {cta.text || t('read_more')}
              </CtaLink>
            </div>
          )}
        </div>
        {image && !videoUrl && (
          <div className="content-banner-section__image-container">
            <picture>
              <source
                media="(max-width: 450px)"
                srcSet={image.url ? imageSizes.small : defaultContentCardsImage.url}
              />
              <source
                media="(mmax-width: 650px)"
                srcSet={image.url ? imageSizes.medium : defaultContentCardsImage.url}
              />

              <img
                src={image.url ? imageSizes.large : defaultContentCardsImage.url}
                alt={image.url ? imageSizes.altText : defaultContentCardsImage.altText}
                className="content-banner-section__image"
              />
            </picture>
          </div>
        )}
        {videoUrl && (
          <div className="content-banner-section__media-wrapper">
            <TeliaVideoPlayer
              src={videoUrl || ''}
              autoplay
              loop
              muted
              controls
              slot="video"
              type="html"
              poster={image.url || ''}
              aria-label={title}
              className="content-banner-section__media-player"
              dataTestid="content-banner-section-video"
            />
          </div>
        )}
      </section>
      {isDividerActive && <TeliaDivider />}
    </>
  );
};

export default ContentBanner;
